import * as React from 'react';
import { createRoute, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChecklistItem, GateStatus, SnapshotSummary } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const periodCloseDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/period-close/$month',
  component: PeriodCloseDetailPage,
});

const GATE_VARIANT: Record<GateStatus, 'default' | 'muted' | 'destructive'> = {
  pass: 'default',
  warn: 'muted',
  fail: 'destructive',
  not_applicable: 'muted',
};

const GATE_LABEL: Record<GateStatus, string> = {
  pass: 'Pass',
  warn: 'Warn',
  fail: 'Fail',
  not_applicable: 'N/A',
};

function PeriodCloseDetailPage(): JSX.Element {
  const { month } = useParams({ from: periodCloseDetailRoute.id });
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);
  const [reopenOpen, setReopenOpen] = React.useState(false);
  const [reopenReason, setReopenReason] = React.useState('');
  const [snapshotDialog, setSnapshotDialog] = React.useState<SnapshotSummary | null>(null);

  const checklistQuery = useQuery({
    queryKey: ['period-close', 'checklist', month],
    queryFn: () => api.getPeriodChecklist(month),
  });

  const snapshotsQuery = useQuery({
    queryKey: ['period-close', 'snapshots', month],
    queryFn: () => api.listPeriodSnapshots(month),
  });

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['period-close', 'checklist', month] }),
      queryClient.invalidateQueries({ queryKey: ['period-close', 'snapshots', month] }),
      queryClient.invalidateQueries({ queryKey: ['period-close', 'list'] }),
    ]);
  };

  const softCloseMutation = useMutation({
    mutationFn: () => api.softClosePeriod(month),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (err) => setError(toMessage(err)),
  });

  const closeMutation = useMutation({
    mutationFn: () => api.closePeriod(month),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (err) => setError(toMessage(err)),
  });

  const reopenMutation = useMutation({
    mutationFn: (reason: string) => api.reopenPeriod(month, { reason }),
    onSuccess: async () => {
      setError(null);
      setReopenOpen(false);
      setReopenReason('');
      await invalidate();
    },
    onError: (err) => setError(toMessage(err)),
  });

  const checklist = checklistQuery.data;
  const items: ChecklistItem[] = checklist?.items ?? [];
  const snapshots = snapshotsQuery.data ?? [];

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            {month}
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            {checklist ? (
              <>
                Status: <span className="font-medium">{checklist.periodStatus}</span>
                {checklist.reopenCount > 0 ? ` · reopened ${checklist.reopenCount}x` : ''}
              </>
            ) : (
              'Loading…'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={!checklist?.canSoftClose || softCloseMutation.isPending}
            onClick={() => softCloseMutation.mutate()}
          >
            {softCloseMutation.isPending ? 'Working…' : 'Soft-close'}
          </Button>
          <Button
            disabled={!checklist?.canClose || closeMutation.isPending}
            onClick={() => closeMutation.mutate()}
          >
            {closeMutation.isPending ? 'Working…' : 'Close'}
          </Button>
          <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
            <DialogTrigger asChild>
              <Button variant="destructive" disabled={!checklist?.canReopen}>
                Reopen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reopen {month}</DialogTitle>
                <DialogDescription>
                  Reopening will mark all snapshots for this month as <code>restated</code>. A reason is required.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-1.5">
                <Label>Reason</Label>
                <Input
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.currentTarget.value)}
                  placeholder="e.g. late journal entry from Finance"
                />
              </div>
              <DialogFooter>
                <Button
                  variant="destructive"
                  disabled={reopenReason.trim().length === 0 || reopenMutation.isPending}
                  onClick={() => reopenMutation.mutate(reopenReason.trim())}
                >
                  {reopenMutation.isPending ? 'Reopening…' : 'Reopen'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Checklist</CardTitle>
          <CardDescription>
            Blocking gates must pass before hard-close. Warnings are informational.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {items.map((item) => (
                <li key={item.key} className="flex items-start justify-between gap-3 py-3 text-sm">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 font-medium">
                      {item.label}
                      {item.blocking ? (
                        <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                          blocking
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{item.key}</div>
                    {item.details && Object.keys(item.details).length > 0 ? (
                      <pre className="mt-1 overflow-x-auto rounded bg-[hsl(var(--muted))] p-2 text-[11px] leading-tight">
                        {JSON.stringify(item.details, null, 2)}
                      </pre>
                    ) : null}
                  </div>
                  <Badge variant={GATE_VARIANT[item.status]}>{GATE_LABEL[item.status]}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Snapshots</CardTitle>
          <CardDescription>
            {snapshots.length} snapshot{snapshots.length === 1 ? '' : 's'} · created at close-time
          </CardDescription>
        </CardHeader>
        <CardContent>
          {snapshots.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              No snapshots yet — snapshots are generated when this month is closed.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {snapshots.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                  <div>
                    <div className="font-medium">{s.kind}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {new Date(s.createdAt).toISOString().slice(0, 19).replace('T', ' ')} UTC
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {s.restated ? <Badge variant="muted">restated</Badge> : null}
                    <Button variant="ghost" size="sm" onClick={() => setSnapshotDialog(s)}>
                      View payload
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={snapshotDialog !== null} onOpenChange={(open) => !open && setSnapshotDialog(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{snapshotDialog?.kind} snapshot</DialogTitle>
            <DialogDescription>
              {snapshotDialog
                ? `Created ${new Date(snapshotDialog.createdAt).toISOString().slice(0, 19).replace('T', ' ')} UTC`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <pre className="max-h-[60vh] overflow-auto rounded bg-[hsl(var(--muted))] p-3 text-xs leading-tight">
            {snapshotDialog ? JSON.stringify(snapshotDialog.payload, null, 2) : ''}
          </pre>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
